import { Field } from "formik";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import EyeCloseIcon from "../../assets/icons/eye-close-icon";
import EyeOpenIcon from "../../assets/icons/eye-open-icon";
import StarIcon from "../../assets/icons/req-star";
import ErrorMsg from "./error-msg";
import "./form.css";
import errorToast from "../helper-components/error-toast";

export default function InputField({
  inputClass = "",
  containerClass = "mb-2 position-relative",
  parentClass = "flex items-center m-0 p-0 w-full",
  labelClass = "whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]",
  defaultInputClass = "min-h-[42px] rounded-[8px]  px-2  bg-[var(--input-bg)] text-[var(--input-color)] text-[length:var(--input-font-size)] font-[var(--input-font-weight)] rounded input-field-no-spin-buttons placeholder:text-[var(--input-place-color)] placeholder:text-[length:var(--input-place-font-size)] placeholder:font-[var(--input-place-font-weight)] focus:outline-none focus:border-[var(--input-focus)] focus:text-[var(--input-color)] focus:bg-[var(--input-bg)] ",
  errorBorder = "border border-[var(--input-error-border)]",
  inputBorder = "border border-[var(--input-border)]",
  max = "",
  min = 0,
  isDisabled = false,
  name = "",
  placeholder = "Enter your email address",
  label = "Email Address",
  id,
  type = "text", // supports: "number" (int only), "float" (decimals)
  errMsgContainerClass = "",
  isContainer = true,
  isUnit = false,
  unitType = localStorage?.getItem("currency") ?? "₹",
  unitPosition = "left",
  isPasswordEye = false,
  passwordEyeStyle = { top: "44%" },
  autoComplete = "",
  unitClassName = "",
  children,
  isRequired = false,
  labelWidth = "10rem",
  prefixicon = "",

  maxValue,
  decimal, // only for type="float": 2,3,... undefined => any decimals

  autoFocus = false,
  maxLength = 1000,

  onKeyDown,
  onBlur,

  unitContainerClass = "flex unit_input__container",
  unitContainerErrorClass = "flex unit_input_container__error",
  passwordEyeClass = "position-absolute  end-0 translate-middle  cursor_pointer",
  inputRef,

  rightIcon = null,
  rightIconClass = "absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer flex items-center",
  rightIconStyle = {},
}) {
  const finalInputClass = inputClass
    ? `${defaultInputClass} ${inputClass}`
    : defaultInputClass;

  const Container = isContainer ? "div" : Fragment;
  const UnitContainer = isUnit ? "div" : Fragment;
  const PasswordEyeContainer = isPasswordEye || rightIcon ? "div" : Fragment;

  const [passInputType, setPassInputType] = useState("password");
  const hasRightAdornment = isPasswordEye || rightIcon;

  const isInt = type === "number";
  const isFloat = type === "float";

  const effectiveMax = useMemo(() => {
    if (typeof maxValue === "number") return maxValue;
    if (max === "" || max === null || max === undefined) return undefined;
    const n = Number(max);
    return Number.isFinite(n) ? n : undefined;
  }, [maxValue, max]);

  const isControlKey = (e) => {
    const k = e.key;
    return (
      k === "Backspace" ||
      k === "Delete" ||
      k === "Tab" ||
      k === "Enter" ||
      k === "Escape" ||
      k === "ArrowLeft" ||
      k === "ArrowRight" ||
      k === "ArrowUp" ||
      k === "ArrowDown" ||
      k === "Home" ||
      k === "End" ||
      (e.ctrlKey || e.metaKey)
    );
  };

  const sanitizeInteger = (v) => {
    const s = String(v ?? "");
    // IMPORTANT: if user types/pastes "1000.111" => keep only "1000"
    const beforeDot = s.split(".")[0];
    return beforeDot.replace(/[^0-9]/g, "");
  };

  const sanitizeFloat = (v) => {
    let s = String(v ?? "");
    s = s.replace(/[^0-9.]/g, "");

    // keep only first dot
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      const before = s.slice(0, firstDot + 1);
      const after = s.slice(firstDot + 1).replace(/\./g, "");
      s = before + after;
    }

    // limit decimals if decimal is provided
    if (
      typeof decimal === "number" &&
      Number.isFinite(decimal) &&
      decimal >= 0
    ) {
      const parts = s.split(".");
      const intPart = parts[0] ?? "";
      const decPart = parts[1] ?? "";
      if (parts.length === 1) return intPart;
      return `${intPart}.${decPart.slice(0, decimal)}`;
    }

    return s;
  };

  const clampToMax = (raw) => {
    if (effectiveMax === undefined) return raw;
    if (raw === "" || raw === ".") return raw;

    const n = Number(raw);
    if (!Number.isFinite(n)) return raw;

    return n > effectiveMax ? String(effectiveMax) : raw;
  };

  const internalOnKeyDown = (e) => {
    if (isControlKey(e)) return;

    if (isInt) {
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
        //errorToast("Only numbers are allowed");
      }
      return;
    }

    if (isFloat) {
      if (e.key === ".") {
        const current = String(e.target?.value ?? "");
        if (current.includes(".")) {
          e.preventDefault();
          //errorToast("Only one decimal point is allowed");
        }
        return;
      }

      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
        //errorToast("Only numbers are allowed");
        return;
      }

      // prevent typing beyond decimal limit (best effort)
      if (
        typeof decimal === "number" &&
        Number.isFinite(decimal) &&
        decimal >= 0
      ) {
        const el = e.target;
        const value = String(el?.value ?? "");
        const start = el?.selectionStart ?? value.length;
        const end = el?.selectionEnd ?? value.length;

        const nextValue =
          value.slice(0, start) + e.key + value.slice(end);

        const dotIndex = nextValue.indexOf(".");
        if (dotIndex !== -1) {
          const decimalsCount = nextValue.length - dotIndex - 1;
          if (decimalsCount > decimal) {
            e.preventDefault();
            //errorToast(`Only ${decimal} decimal places allowed`);
          }
        }
      }
      return;
    }

    // default text behaviour: block special chars
    const validKey = /^[a-zA-Z0-9\s]+$/;
    if (!validKey.test(e.key)) {
      e.preventDefault();
      //errorToast("Special Characters are blocked");
    }
  };

  useEffect(() => {
    if (autoFocus && inputRef?.current) inputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use text for number/float to avoid browser "e", "-", etc.
  const inputTypeAttr =
    type === "password" ? passInputType : isInt || isFloat ? "text" : type;

  const inputMode = isInt ? "numeric" : isFloat ? "decimal" : undefined;
  const pattern = isInt
    ? "[0-9]*"
    : isFloat
    ? "[0-9]*[.]?[0-9]*"
    : undefined;

  return (
    <Container
      {...(isContainer && containerClass ? { className: containerClass } : {})}
    >
      <Field className="no-arrows" name={name}>
        {({ field, meta }) => (
          <div className={parentClass}>
            {label && (
              <label
                style={{ minWidth: labelWidth }}
                {...(id ? { htmlFor: id } : {})}
                className={`${labelClass} flex  flex-nowrap`}
              >
                <span className="flex items-center gap-2">
                  {prefixicon}
                  {label}
                </span>
                {isRequired && (
                  <span className="ml-2">
                    <StarIcon />
                  </span>
                )}
              </label>
            )}

            <UnitContainer
              {...(unitContainerClass && isUnit
                ? {
                    className: `  ${
                      meta.touched && meta.error
                        ? unitContainerErrorClass
                        : unitContainerClass
                    } ${
                      unitPosition === "right" ? "flex-row-reverse" : ""
                    } w-full`,
                  }
                : {})}
            >
              {isUnit && (
                <div
                  className={`d-flex justify-content-center align-items-center font_poppins mt-1 ${
                    unitPosition === "right" ? "flex-row-reverse" : ""
                  }`}
                >
                  <span
                    className={` fw_500 text-sm align-middle primary_color ${unitClassName} ${
                      unitPosition === "right" ? "ms-1" : "me-1"
                    }`}
                  >
                    {unitType}
                  </span>{" "}
                  {unitPosition !== "image" && (
                    <span className={`input-vr mx-2`}></span>
                  )}
                </div>
              )}

              <PasswordEyeContainer
                {...(isPasswordEye || rightIcon
                  ? { className: `position-relative relative w-full` }
                  : {})}
              >
                <input
                  min={min}
                  max={max}
                  maxLength={maxLength}
                  onWheel={(e) => e.target.blur()}
                  ref={inputRef}
                  {...(isDisabled ? { disabled: true } : {})}
                  {...(autoComplete ? { autoComplete } : {})}
                  type={inputTypeAttr}
                  inputMode={inputMode}
                  pattern={pattern}
                  name={field.name}
                  value={field.value ?? ""}  // ✅ important for visibility
                  placeholder={placeholder}
                  {...(id ? { id } : {})}
                  className={`${
                    isUnit
                      ? `${finalInputClass} focus_outline__none w-100 ${
                          hasRightAdornment ? "pr-10" : ""
                        }`
                      : `${
                          meta.touched && meta.error ? errorBorder : inputBorder
                        } ${
                          autoFocus
                            ? "focus:border-focus-color border"
                            : "no-arrows"
                        } ${finalInputClass} input-field-no-spin-buttons w-full ${
                          hasRightAdornment ? "pr-10" : ""
                        }`
                  } ${
                    isDisabled
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : ""
                  }`}
                  onKeyDown={(e) => {
                    internalOnKeyDown(e);
                    if (typeof onKeyDown === "function") onKeyDown(e);
                  }}
                  onChange={(e) => {
                    let v = e.target.value ?? "";

                    if (isInt) v = sanitizeInteger(v);
                    if (isFloat) v = sanitizeFloat(v);

                    v = clampToMax(v);

                    if (String(v).length > maxLength) {
                      v = String(v).slice(0, maxLength);
                    }

                    // ✅ ALWAYS update Formik using a clean event object
                    field.onChange({ target: { name: field.name, value: v } });
                  }}
                  onPaste={(e) => {
                    if (!isInt && !isFloat) return;

                    e.preventDefault();
                    const text = e.clipboardData?.getData("text") ?? "";

                    let v = text;
                    if (isInt) v = sanitizeInteger(v);
                    if (isFloat) v = sanitizeFloat(v);

                    v = clampToMax(v);

                    field.onChange({ target: { name: field.name, value: v } });
                  }}
                  onBlur={(e) => {
                    // ✅ keep Formik touched/blur behaviour
                    field.onBlur(e);
                    if (typeof onBlur === "function") onBlur(e);
                  }}
                />

                {isPasswordEye &&
                  (passInputType === "password" ? (
                    <button
                      style={passwordEyeStyle}
                      type="button"
                      className={`${passwordEyeClass} `}
                    >
                      <EyeCloseIcon onClick={() => setPassInputType("text")} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      style={passwordEyeStyle}
                      className={`${passwordEyeClass} border-0`}
                    >
                      <EyeOpenIcon
                        onClick={() => setPassInputType("password")}
                      />
                    </button>
                  ))}

                {rightIcon && (
                  <div style={rightIconStyle} className={rightIconClass}>
                    {rightIcon}
                  </div>
                )}
              </PasswordEyeContainer>
            </UnitContainer>
          </div>
        )}
      </Field>

      <ErrorMsg containerClass={errMsgContainerClass} name={name} />
      {children}
    </Container>
  );
}
